import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";

// Campo de horário: continua tendo a MESMA lista suspensa de antes, mas o
// valor escolhido pode ser editado por cima. Quem fecha 21:50 não conseguia,
// porque a lista só tem as meias horas.
//
// O valor só é comitado quando o campo perde o foco (ou no Enter): enquanto
// digita, "23:5" é estado intermediário legítimo e não pode virar horário
// gravado. Se o que sobrou não for horário, volta pro último válido —
// businessHours torto fecha a loja no checkout, então este campo nunca
// entrega lixo pra cima.

/** "23:50" → 1430. Null se não for HH:MM válido. */
export function hmToMinutes(value: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** 1430 → "23:50" (fora da faixa, gruda nas bordas do dia). */
export function minutesToHM(total: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(total)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Interpreta o que foi digitado com folga: "2350"/"23:50" → 23:50,
 * "935" → 09:35, "9" → 09:00, "9:5" → 09:05. Null quando não dá pra salvar.
 */
export function parseTypedTime(raw: string): string | null {
  const txt = raw.trim();
  if (!txt) return null;
  const withColon = /^(\d{1,2}):(\d{1,2})$/.exec(txt);
  if (withColon) {
    const h = Number(withColon[1]);
    const m = Number(withColon[2]);
    if (h > 23 || m > 59) return null;
    return minutesToHM(h * 60 + m);
  }
  const digits = txt.replace(/\D/g, "");
  if (!digits || digits.length > 4) return null;
  const h = digits.length <= 2 ? Number(digits) : Number(digits.slice(0, -2));
  const m = digits.length <= 2 ? 0 : Number(digits.slice(-2));
  if (h > 23 || m > 59) return null;
  return minutesToHM(h * 60 + m);
}

interface TimeFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Itens da lista suspensa — os mesmos de antes. */
  options: string[];
  className?: string;
  "data-testid"?: string;
}

export function TimeField({ value, onChange, options, className, ...rest }: TimeFieldProps) {
  const [draft, setDraft] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const selectedRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (open) selectedRef.current?.scrollIntoView({ block: "center" });
  }, [open]);

  const commit = () => {
    if (draft === null) return;
    const parsed = parseTypedTime(draft);
    setDraft(null);
    if (parsed && parsed !== value) onChange(parsed);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={draft ?? value}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                inputRef.current?.blur();
                setOpen(false);
              }
              if (e.key === "Escape") {
                setDraft(null);
                setOpen(false);
              }
            }}
            className="w-full border rounded-md py-2 pl-2 pr-8 text-sm bg-background h-10"
            {...rest}
          />
          <button
            type="button"
            // onMouseDown em vez de onClick: o clique não pode roubar o foco
            // antes do onBlur comitar o que já estava digitado.
            onMouseDown={(e) => {
              e.preventDefault();
              commit();
              setOpen((v) => !v);
            }}
            className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground hover:text-foreground"
            aria-label="Escolher horário da lista"
            tabIndex={-1}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="p-1 w-[var(--radix-popover-trigger-width)] min-w-24 max-h-60 overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {options.map((t) => (
          <button
            key={t}
            type="button"
            ref={t === value ? selectedRef : undefined}
            onClick={() => {
              setDraft(null);
              if (t !== value) onChange(t);
              setOpen(false);
            }}
            className={cn(
              "w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted",
              t === value && "bg-muted font-semibold",
            )}
          >
            {t}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
